import { __awaiter, __generator } from "tslib";
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { fetchConsumerInstallInfoConfig } from '@office-start/consumer-install-service';
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { fetchIdentity } from '@office-start/identity-service';
import { logErrorEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { fetchLocStrings } from '@office-start/loc-service';
import { shouldShowSurfaceContent } from '@office-start/surface-content-utility';
import { logError } from '@office-start/telemetry-service';
import { fetchUserEntitlements } from '@office-start/user-entitlements-service';
import { calculateFirstRunType } from './calculateFirstRunType';
import { generateAriaLabels } from './generators/generateAriaLabels';
import { generateGreetings } from './generators/generateGreetings';
import { generateIcons } from './generators/generateIcons';
import { generateSlideGroups } from './generators/generateSlideGroups';
export function resolveFirstRunConfig(isSecondRun) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function () {
        var fetchDataPromisesObject, firstRunType, featureGates, _c, environment, workload, deploymentEnvironment, m365StringLogoEnabled, isDevEnvironment, parseMockConfig, userEntitlements, consumerInstallInfoConfig, identity, isPipl, locStrings, officeComponentContext, showSurfaceContent, firstRunMockSurfaceMessagingEnabled, surfaceContentEnabled, ariaLabels, icons, greetings, slideGroups, err_1, message;
        var _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 13, , 14]);
                    fetchDataPromisesObject = {
                        consumerInstallInfoConfigPromise: fetchConsumerInstallInfoConfig(),
                        featureGatesPromise: fetchFeatureGates(),
                        locStringsPromise: fetchLocStrings(),
                        userEntitlementsPromise: fetchUserEntitlements(),
                        officeComponentContextPromise: fetchOfficeComponentContext(),
                        fetchIdentityPromise: fetchIdentity(),
                    };
                    firstRunType = void 0;
                    return [4 /*yield*/, fetchDataPromisesObject.featureGatesPromise];
                case 1:
                    featureGates = (_e.sent()).result;
                    return [4 /*yield*/, fetchDataPromisesObject.officeComponentContextPromise];
                case 2:
                    _c = _e.sent(), environment = _c.deploymentEnvironment, workload = _c.workload;
                    deploymentEnvironment = environment.toLowerCase();
                    m365StringLogoEnabled = !!((_a = featureGates.m365StringLogoGates) === null || _a === void 0 ? void 0 : _a.m365StringLogoEnabled);
                    isDevEnvironment = deploymentEnvironment === "prodprv" /* OfficeEnvironment.ProdPrv */ ||
                        deploymentEnvironment === "ppe" /* OfficeEnvironment.PPE */ ||
                        deploymentEnvironment === "dev" /* OfficeEnvironment.Dev */;
                    if (!isDevEnvironment) return [3 /*break*/, 4];
                    return [4 /*yield*/, import('./helpers/parseMockConfig')];
                case 3:
                    parseMockConfig = (_e.sent()).parseMockConfig;
                    firstRunType = parseMockConfig(featureGates);
                    _e.label = 4;
                case 4: return [4 /*yield*/, fetchDataPromisesObject.userEntitlementsPromise];
                case 5:
                    userEntitlements = (_e.sent()).result;
                    if (!(firstRunType === undefined)) return [3 /*break*/, 8];
                    return [4 /*yield*/, fetchDataPromisesObject.consumerInstallInfoConfigPromise];
                case 6:
                    consumerInstallInfoConfig = _e.sent();
                    return [4 /*yield*/, fetchDataPromisesObject.fetchIdentityPromise];
                case 7:
                    identity = _e.sent();
                    isPipl = (identity === null || identity === void 0 ? void 0 : identity.isPipl) || false;
                    if (!userEntitlements) {
                        throw new Error('UserEntitlements not found, unable to calculate FirstRunType');
                    }
                    firstRunType = calculateFirstRunType(userEntitlements, consumerInstallInfoConfig === null || consumerInstallInfoConfig === void 0 ? void 0 : consumerInstallInfoConfig.currentSkuInfo.skuType, isPipl);
                    _e.label = 8;
                case 8: return [4 /*yield*/, fetchDataPromisesObject.locStringsPromise];
                case 9:
                    locStrings = _e.sent();
                    return [4 /*yield*/, fetchDataPromisesObject.officeComponentContextPromise];
                case 10:
                    officeComponentContext = _e.sent();
                    return [4 /*yield*/, shouldShowSurfaceContent(officeComponentContext.workload)];
                case 11:
                    showSurfaceContent = _e.sent();
                    firstRunMockSurfaceMessagingEnabled = !!((_b = featureGates.firstRunGates) === null || _b === void 0 ? void 0 : _b.firstRunMockSurfaceMessagingEnabled);
                    surfaceContentEnabled = showSurfaceContent || firstRunMockSurfaceMessagingEnabled;
                    ariaLabels = generateAriaLabels(locStrings);
                    icons = generateIcons(locStrings, m365StringLogoEnabled, isSecondRun);
                    greetings = generateGreetings(locStrings, userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.UserGreetingName, isSecondRun);
                    return [4 /*yield*/, generateSlideGroups(workload, firstRunType, locStrings, surfaceContentEnabled, userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.UserGreetingName, userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.CompanyDisplayName, isSecondRun)];
                case 12:
                    slideGroups = _e.sent();
                    return [2 /*return*/, {
                            firstRunType: firstRunType,
                            ariaLabels: ariaLabels,
                            icons: icons,
                            greetings: greetings,
                            slideGroups: slideGroups,
                        }];
                case 13:
                    err_1 = _e.sent();
                    message = "Couldn't resolve FirstRunConfig: ".concat(err_1);
                    logErrorEvent(KnownInstrumentationEvents.FirstRunResolveConfigError, (_d = {},
                        _d["Error" /* InstrumentationParam.Error */] = message,
                        _d));
                    // TODO: Remove above telemetry once migrated over to OTEL
                    logError({
                        eventName: KnownInstrumentationEvents.FirstRunResolveConfigError,
                        errorType: 'FirstRun_Error',
                        area: 'FirstRun',
                        errorCategory: 'React',
                        message: message,
                    });
                    return [2 /*return*/, null];
                case 14: return [2 /*return*/];
            }
        });
    });
}
//# sourceMappingURL=resolveFirstRunConfig.js.map